/**
 * Real-time match chats unread counter.
 *
 * Connects to the shared Socket.IO server, registers the user in their
 * per-user room (`user_{uid}`), and listens to `match_chat_unread_increment`
 * to bump the badge instantly when a new message arrives in any chat where
 * the user is a participant.
 *
 * Falls back to REST polling on initial mount and as a safety net every 60s.
 */
import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { matchesApi } from './api';
import { playNotificationSound } from './notificationSound';

export default function useMatchChatsUnread(user, eligible) {
  const [unread, setUnread] = useState(0);
  const socketRef = useRef(null);
  const lastSeenRef = useRef(new Set()); // recent message ids dedup

  useEffect(() => {
    if (!user || !eligible) {
      setUnread(0);
      return;
    }

    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const res = await matchesApi.getMatchChatsUnread();
        // Show number of CHATS with unread messages (max 9+), not total
        // message count. A user with 5 chats of 20 messages each should see
        // "5" on the badge, not "100" — that was overwhelming and made the
        // badge look "stuck on".
        if (!cancelled) setUnread(res.data?.chats_with_unread || 0);
      } catch { /* silent */ }
    };
    fetchUnread();

    // Safety-net poll every 60s in case socket misses an event
    const interval = setInterval(fetchUnread, 60000);

    // Refresh on tab regain focus
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchUnread();
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Refresh when any chat is marked as read (fired by MatchChatDialog)
    const onChatRead = () => fetchUnread();
    window.addEventListener('match_chat_read', onChatRead);

    // Socket
    const BACKEND_URL = window.location.origin;
    const socket = io(BACKEND_URL, {
      path: '/api/sio/',
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('register_user', {
        user_id: user.user_id,
        gamertag: user.gamertag,
        name: user.name,
        role: user.role,
      });
    });

    socket.on('match_chat_unread_increment', (payload) => {
      if (cancelled) return;
      // Skip messages sent by self (defensive)
      if (payload?.sender_id && payload.sender_id === user.user_id) return;
      // Skip system-sent notifications — only real human messages bump the badge
      if (payload?.sender_id === 'system' || payload?.is_system) return;
      const key = `${payload?.match_id}:${payload?.created_at}`;
      if (lastSeenRef.current.has(key)) return;
      lastSeenRef.current.add(key);
      // Limit dedup set size
      if (lastSeenRef.current.size > 200) {
        const first = lastSeenRef.current.values().next().value;
        lastSeenRef.current.delete(first);
      }
      // Socket events arrive per-message but badge counts CHATS, so we
      // refetch the authoritative count from the server (cheap call) to
      // ensure the badge tracks chats_with_unread, not raw message count.
      fetchUnread();
      // Soft chat tone (silent if browser blocks autoplay)
      playNotificationSound({ pitch: 'low' });
    });

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('match_chat_read', onChatRead);
      try { socket.disconnect(); } catch { /* ignore */ }
    };
  }, [user, eligible]);

  // Allow consumers to manually refresh (e.g. after marking a chat read)
  const refresh = async () => {
    try {
      const res = await matchesApi.getMatchChatsUnread();
      setUnread(res.data?.chats_with_unread || 0);
    } catch { /* silent */ }
  };

  return { unread, refresh };
}
